import { PlusIcon } from '@commenda/icons';
import {
  Badge,
  Button,
  IconButton,
  Icons,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  TagInput,
  Textarea,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
// import { DeleteGroupModal, useNotification } from '@commenda/ui';
import { SECURE_INFORMATION_REQUEST_FIELD_TYPE_TO_TEXT } from '@commenda/utils';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { SecureInformationRequestFieldType } from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { DeleteGroupModal } from './DeleteGroupModal';
import { FieldProps, GroupProps } from './types';

interface SecureInformationRequestFormBuilderProps {
  groups: GroupProps[];
  setGroups: React.Dispatch<React.SetStateAction<GroupProps[]>>;
}

const SecureInformationRequestFormBuilder = ({
  groups,
  setGroups,
}: SecureInformationRequestFormBuilderProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(
    new Set([0])
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const deleteGroupModalVisibility = useToggle();
  const [groupIndexToDelete, setGroupIndexToDelete] = useState<number | null>(
    null
  );

  const { setNotification } = useNotification();

  const isGroupCompleted = (group: GroupProps) => {
    if (!group.headerText || group.headerText.trim() === '') return false;

    if (!group.fields || group.fields.length === 0) return false;

    return group.fields.every((field) => {
      if (!field.headerText || field.headerText.trim() === '') return false;

      if (!field.type) return false;

      if (
        field.type === SecureInformationRequestFieldType.SELECT ||
        field.type === SecureInformationRequestFieldType.MULTI_SELECT
      )
        return field.selectOptions && field.selectOptions.length > 0;

      return true;
    });
  };

  const toggleGroup = (index: number) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const addGroup = () => {
    const newField: FieldProps = {
      id:
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`,
      headerText: '',
      isSecure: false,
      type: '' as SecureInformationRequestFieldType,
      indexInGroup: 0,
      tooltip: '',
    };

    const newGroup: GroupProps = {
      id:
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`,
      headerText: '',
      fields: [newField],
      indexInRequest: groups.length,
    };
    setGroups((prev) => [...prev, newGroup]);
    // Don't auto-expand new groups, only expand the first group by default
    if (groups.length === 0) setExpandedGroups(new Set([0]));
  };

  const addField = (groupIndex: number) => {
    setGroups((prev) => {
      const newGroups = [...prev];
      const newField: FieldProps = {
        id:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`,
        headerText: '',
        isSecure: false,
        type: '' as SecureInformationRequestFieldType,
        indexInGroup: newGroups[groupIndex].fields.length,
        tooltip: '',
      };
      newGroups[groupIndex].fields.push(newField);
      return newGroups;
    });
  };

  const updateGroupName = (groupIndex: number, value: string) => {
    setGroups((prev) => {
      const newGroups = [...prev];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        headerText: value,
      };
      return newGroups;
    });
  };

  const updateField = (
    groupIndex: number,
    fieldIndex: number,
    updates: Partial<FieldProps>
  ) => {
    setGroups((prev) => {
      const newGroups = [...prev];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        fields: newGroups[groupIndex].fields.map((field, index) =>
          index === fieldIndex ? { ...field, ...updates } : field
        ),
      };
      return newGroups;
    });
  };

  const deleteField = (groupIndex: number, fieldIndex: number) => {
    setGroups((prev) => {
      const newGroups = [...prev];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        fields: newGroups[groupIndex].fields
          .filter((_, index) => index !== fieldIndex)
          .map((field, index) => ({ ...field, indexInGroup: index })),
      };
      return newGroups;
    });
  };

  const deleteGroup = (groupIndex: number) => {
    setGroups((prev) => {
      const newGroups = prev.filter((_, index) => index !== groupIndex);
      return newGroups.map((group, index) => ({
        ...group,
        indexInRequest: index,
      }));
    });
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      newSet.delete(groupIndex);
      return newSet;
    });

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Group deleted successfully.',
    });
  };

  const resetGroup = (groupIndex: number) => {
    setGroups((prev) => {
      const newGroups = [...prev];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        headerText: '',
        fields: [],
      };
      return newGroups;
    });
  };

  const onDragStart = (start: {
    draggableId: string;
    source: { droppableId: string; index: number };
  }) => {
    if (groups.length <= 1) return;
    setIsDragging(true);

    // Collapse the group being dragged
    const draggedGroupIndex = start.source.index;
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      newSet.delete(draggedGroupIndex);
      return newSet;
    });
  };

  const onDragUpdate = (update: {
    destination: { droppableId: string; index: number } | null;
  }) => {
    if (groups.length <= 1) return;
    if (
      update.destination &&
      update.destination.droppableId === 'groups-list'
    ) {
      const newIndex = update.destination.index;
      setDragOverIndex(newIndex);
    } else {
      setDragOverIndex(null);
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (groups.length <= 1) return;
    // Reset drag state
    setIsDragging(false);
    setDragOverIndex(null);

    if (!result.destination) return;

    const { source, destination } = result;

    if (
      source.droppableId === 'groups-list' &&
      destination.droppableId === 'groups-list'
    ) {
      setGroups((prev) => {
        const newGroups = Array.from(prev);
        const [reorderedGroup] = newGroups.splice(source.index, 1);
        newGroups.splice(destination.index, 0, reorderedGroup);

        // Update indexInRequest for all groups
        const updatedGroups = newGroups.map((group, index) => ({
          ...group,
          indexInRequest: index,
        }));

        return updatedGroups;
      });

      // Update expanded groups state to reflect new positions
      setExpandedGroups((prev) => {
        const newExpandedGroups = new Set<number>();
        prev.forEach((expandedIndex) => {
          if (expandedIndex === source.index) {
            // The dragged group should be expanded at its new position
            newExpandedGroups.add(destination.index);
          } else if (
            expandedIndex > source.index &&
            expandedIndex <= destination.index
          ) {
            // Groups that were after the source and before/at the destination move up
            newExpandedGroups.add(expandedIndex - 1);
          } else if (
            expandedIndex < source.index &&
            expandedIndex >= destination.index
          ) {
            // Groups that were before the source and after/at the destination move down
            newExpandedGroups.add(expandedIndex + 1);
          } else {
            // Groups that weren't affected by the drag keep their state
            newExpandedGroups.add(expandedIndex);
          }
        });
        return newExpandedGroups;
      });
    }
  };

  const fieldTypeOptions = Object.values(SecureInformationRequestFieldType).map(
    (type) => ({
      label: SECURE_INFORMATION_REQUEST_FIELD_TYPE_TO_TEXT[type],
      value: type,
    })
  );

  return (
    <div className="flex flex-col gap-6">
      <h2 className="txt-medium-medium text-text-quaternary">FORM BUILDER</h2>

      <DeleteGroupModal
        isShown={deleteGroupModalVisibility.visible}
        onClose={() => {
          setGroupIndexToDelete(null);
          deleteGroupModalVisibility.off();
        }}
        onDelete={() => {
          if (groupIndexToDelete !== null) {
            deleteGroup(groupIndexToDelete);
          }
          setGroupIndexToDelete(null);
          deleteGroupModalVisibility.off();
        }}
        groupName={
          groupIndexToDelete !== null
            ? groups[groupIndexToDelete]?.headerText ||
              `Group ${groupIndexToDelete + 1}`
            : ''
        }
      />

      <DragDropContext
        onDragStart={onDragStart}
        onDragUpdate={onDragUpdate}
        onDragEnd={onDragEnd}
      >
        <div className="w-full">
          <Droppable droppableId="groups-list" type="GROUP">
            {(provided) => {
              return (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-4"
                  style={{
                    minHeight: '50px',
                  }}
                >
                  {groups.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      No groups yet. Click &quot;Add another group&quot; to get
                      started.
                    </div>
                  ) : (
                    groups.map((group, groupIndex) => (
                      <div key={group.id || `group-container-${groupIndex}`}>
                        {/* Blue line at the top when dragging to position 0 */}
                        {groups.length > 1 &&
                          isDragging &&
                          dragOverIndex === 0 &&
                          groupIndex === 0 && (
                            <div className="h-1 bg-blue-500 rounded-full mb-4 mx-4 shadow-lg" />
                          )}
                        <Draggable
                          key={group.id || `group-${groupIndex}`}
                          draggableId={group.id || `group-${groupIndex}`}
                          index={groupIndex}
                          isDragDisabled={groups.length <= 1}
                        >
                          {(dragProvided, dragSnapshot) => {
                            return dragSnapshot.isDragging ? (
                              /* Dragged State - Compact appearance */
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                className="w-fit !max-h-[270px] max-w-[320px] truncate text-center shadow-xl flex items-center border border-border-primary rounded-xl cursor-grab py-lg px-2xl h-fit bg-bg-primary"
                              >
                                <span className="txt-medium-bold flex items-center justify-center w-full text-center">
                                  {group.headerText ||
                                    `Group ${groupIndex + 1}`}
                                </span>
                              </div>
                            ) : (
                              /* Normal State - Full card with all controls */
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                className="flex items-start gap-xs cursor-grab"
                              >
                                {/* Drag Handle - Outside the card */}
                                {groups.length > 1 ? (
                                  <div title="Drag to reorder">
                                    <div className="size-6 content-center flex justify-center items-center translate-y-1">
                                      <div className="grid grid-cols-2 gap-0.5 group">
                                        <div className="size-1.5 bg-fg-quaternary group-hover:bg-fg-quaternary-hover rounded-full shrink-0" />
                                        <div className="size-1.5 bg-fg-quaternary group-hover:bg-fg-quaternary-hover rounded-full shrink-0" />
                                        <div className="size-1.5 bg-fg-quaternary group-hover:bg-fg-quaternary-hover rounded-full shrink-0" />
                                        <div className="size-1.5 bg-fg-quaternary group-hover:bg-fg-quaternary-hover rounded-full shrink-0" />
                                        <div className="size-1.5 bg-fg-quaternary group-hover:bg-fg-quaternary-hover rounded-full shrink-0" />
                                        <div className="size-1.5 bg-fg-quaternary group-hover:bg-fg-quaternary-hover rounded-full shrink-0" />
                                      </div>
                                    </div>
                                  </div>
                                ) : null}

                                {/* Card Content */}
                                <div className="flex-1 border border-border-primary rounded-lg bg-bg-primary overflow-clip">
                                  <div className="flex py-lg px-2xl items-center justify-between bg-bg-secondary">
                                    <div className="flex items-center gap-2">
                                      <IconButton
                                        icon={
                                          expandedGroups.has(groupIndex) ? (
                                            <Icons.ChevronUp />
                                          ) : (
                                            <Icons.ChevronDown />
                                          )
                                        }
                                        size="sm"
                                        aria-label="Expand group"
                                        onClick={() => {
                                          toggleGroup(groupIndex);
                                        }}
                                      />
                                      <span
                                        className={`font-medium ${
                                          dragSnapshot.isDragging
                                            ? 'text-blue-900'
                                            : 'text-gray-900'
                                        }`}
                                      >
                                        {group.headerText ||
                                          `Group ${groupIndex + 1}`}
                                      </span>
                                      <Badge
                                        variant={
                                          isGroupCompleted(group)
                                            ? 'success'
                                            : 'warning'
                                        }
                                        size="sm"
                                      >
                                        {isGroupCompleted(group)
                                          ? 'Completed'
                                          : 'Incomplete'}
                                      </Badge>
                                    </div>
                                    {!dragSnapshot.isDragging && (
                                      <div className="flex items-center gap-1">
                                        {group.fields.length > 0 ? (
                                          <Tooltip content="Reset group">
                                            <Button
                                              variant="tertiary"
                                              size="sm"
                                              onClick={() => {
                                                resetGroup(groupIndex);
                                              }}
                                            >
                                              <Icons.RefreshCw01 className="w-4 h-4" />
                                            </Button>
                                          </Tooltip>
                                        ) : null}
                                        {groups.length > 1 ? (
                                          <Tooltip content="Delete group">
                                            <Button
                                              variant="tertiary"
                                              size="sm"
                                              onClick={() => {
                                                if (groups.length <= 1) return;
                                                const hasAnyFields =
                                                  groups[groupIndex].fields
                                                    .length > 0;
                                                if (hasAnyFields) {
                                                  setGroupIndexToDelete(
                                                    groupIndex
                                                  );
                                                  deleteGroupModalVisibility.on();
                                                } else {
                                                  deleteGroup(groupIndex);
                                                }
                                              }}
                                              className="text-red-600 hover:text-red-700"
                                            >
                                              <Icons.Trash01 className="w-4 h-4" />
                                            </Button>
                                          </Tooltip>
                                        ) : null}
                                      </div>
                                    )}
                                  </div>

                                  {expandedGroups.has(groupIndex) &&
                                    !dragSnapshot.isDragging && (
                                      <div className="space-y-2xl px-2xl py-lg">
                                        <div className="space-y-xs">
                                          <Label
                                            htmlFor={`group-${groupIndex}-name`}
                                          >
                                            Group {groupIndex + 1} name
                                          </Label>
                                          <Input
                                            id={`group-${groupIndex}-name`}
                                            placeholder="Enter input name"
                                            value={group.headerText}
                                            onChange={(e) =>
                                              updateGroupName(
                                                groupIndex,
                                                e.target.value
                                              )
                                            }
                                          />
                                        </div>

                                        <Separator />

                                        <div className="space-y-2xl">
                                          {group.fields.map(
                                            (field, fieldIndex) => (
                                              <div
                                                className="flex flex-col gap-2xl"
                                                key={
                                                  field.id ??
                                                  `field-${groupIndex}-${fieldIndex}`
                                                }
                                              >
                                                <div className="flex flex-col gap-xl">
                                                  <div className="flex items-center justify-between">
                                                    <span className="txt-medium-bold">
                                                      Field {fieldIndex + 1}
                                                    </span>
                                                    {group.fields.length >
                                                      1 && (
                                                      <Button
                                                        variant="destructive-link"
                                                        size="sm"
                                                        onClick={() =>
                                                          deleteField(
                                                            groupIndex,
                                                            fieldIndex
                                                          )
                                                        }
                                                      >
                                                        <Icons.Trash01 className="size-4" />
                                                        <span>
                                                          Delete field
                                                        </span>
                                                      </Button>
                                                    )}
                                                  </div>
                                                  <div className="grid grid-cols-2 gap-3xl">
                                                    <div className="flex flex-col gap-xs">
                                                      <Label
                                                        htmlFor={`field-${groupIndex}-${fieldIndex}-name`}
                                                      >
                                                        Field {fieldIndex + 1}{' '}
                                                        name
                                                      </Label>
                                                      <Input
                                                        placeholder="Enter field name"
                                                        value={field.headerText}
                                                        onChange={(e) =>
                                                          updateField(
                                                            groupIndex,
                                                            fieldIndex,
                                                            {
                                                              headerText:
                                                                e.target.value,
                                                            }
                                                          )
                                                        }
                                                        className="h-11"
                                                      />
                                                    </div>
                                                    <div className="flex flex-col gap-xs">
                                                      <Label
                                                        htmlFor={`field-${groupIndex}-${fieldIndex}-type`}
                                                      >
                                                        Field {fieldIndex + 1}{' '}
                                                        type
                                                      </Label>
                                                      <Select
                                                        value={field.type}
                                                        onValueChange={(val) =>
                                                          updateField(
                                                            groupIndex,
                                                            fieldIndex,
                                                            {
                                                              type: val as SecureInformationRequestFieldType,
                                                            }
                                                          )
                                                        }
                                                      >
                                                        <SelectTrigger className="h-11">
                                                          <SelectValue placeholder="Select type" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                          {fieldTypeOptions.map(
                                                            (option) => (
                                                              <SelectItem
                                                                key={
                                                                  option.value
                                                                }
                                                                value={
                                                                  option.value
                                                                }
                                                              >
                                                                {option.label}
                                                              </SelectItem>
                                                            )
                                                          )}
                                                        </SelectContent>
                                                      </Select>
                                                    </div>
                                                  </div>
                                                  {(field.type ===
                                                    SecureInformationRequestFieldType.SELECT ||
                                                    field.type ===
                                                      SecureInformationRequestFieldType.MULTI_SELECT) && (
                                                    <div className="flex flex-col gap-xs">
                                                      <Label
                                                        htmlFor={`field-${groupIndex}-${fieldIndex}-options`}
                                                      >
                                                        Dropdown options
                                                      </Label>
                                                      <TagInput
                                                        tags={
                                                          field.selectOptions ??
                                                          []
                                                        }
                                                        onTagsChange={(tags) =>
                                                          updateField(
                                                            groupIndex,
                                                            fieldIndex,
                                                            {
                                                              selectOptions:
                                                                tags,
                                                            }
                                                          )
                                                        }
                                                        isEditable
                                                        placeholder="Type an option and press Enter"
                                                        classNames={{
                                                          trigger:
                                                            'min-h-11 w-full',
                                                        }}
                                                      />
                                                    </div>
                                                  )}
                                                  <div className="flex flex-col gap-xs">
                                                    <Label
                                                      htmlFor={`field-${groupIndex}-${fieldIndex}-tooltip`}
                                                    >
                                                      Field {fieldIndex + 1}{' '}
                                                      tooltip information
                                                      (optional)
                                                    </Label>
                                                    <Textarea
                                                      id={`field-${groupIndex}-${fieldIndex}-tooltip`}
                                                      placeholder="Type here"
                                                      value={field.tooltip}
                                                      onChange={(e) =>
                                                        updateField(
                                                          groupIndex,
                                                          fieldIndex,
                                                          {
                                                            tooltip:
                                                              e.target.value,
                                                          }
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                </div>
                                                {group.fields.length !==
                                                fieldIndex + 1 ? (
                                                  <Separator />
                                                ) : null}
                                              </div>
                                            )
                                          )}
                                          <div className="flex justify-between">
                                            <span />
                                            <Button
                                              type="button"
                                              variant="link"
                                              size="sm"
                                              onClick={() =>
                                                addField(groupIndex)
                                              }
                                              className="flex items-center gap-2 self"
                                            >
                                              <PlusIcon className="w-4 h-4" />
                                              {group.fields.length === 0
                                                ? 'Add field'
                                                : 'Add another field'}
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            );
                          }}
                        </Draggable>
                        {/* Blue line below each card when dragging to the next position */}
                        {groups.length > 1 &&
                          isDragging &&
                          dragOverIndex === groupIndex + 1 &&
                          dragOverIndex < groups.length && (
                            <div className="h-1 bg-blue-500 rounded-full mt-4 mx-4 shadow-lg" />
                          )}
                      </div>
                    ))
                  )}
                  {/* Blue line at the bottom when dragging to the end */}
                  {groups.length > 1 &&
                    isDragging &&
                    dragOverIndex >= groups.length &&
                    groups.length > 0 && (
                      <div className="h-1 bg-blue-500 rounded-full mt-4 mx-4 shadow-lg" />
                    )}
                  {provided.placeholder}
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={addGroup}
                    className="flex items-center gap-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add another group
                  </Button>
                </div>
              );
            }}
          </Droppable>
        </div>
      </DragDropContext>
    </div>
  );
};

export { SecureInformationRequestFormBuilder };
